// Module 16(b) §7 — the feature's single state container.
//
// NO PERSISTENCE, deliberately (contract §7 / §18). Jobs and limits are `{accountUserId,
// workspaceId}`-scoped and a stale remaining-export count would tell the user they can create an
// export they cannot; the `draft` is in-memory only because a persisted one would submit workspace
// A's account ids under workspace B. `registerDataExportLifecycle` wires `reset()` to both the
// workspace switch and logout.
//
// It is a container, not an HTTP client: every fetch goes through `exportJobService`.
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type {
  ExportJob,
  ExportJobDraft,
  ExportLimits,
  ExportTemplate,
} from '../models/exportJob.types'
import { createExportJobDraft } from '../service/exportDraft'
import { exportJobService } from '../service/exportJobService'

import { useAccount, useRbac, useWorkspace } from '@/features/account'

/** The package entitlements that carry the usage denominators (contract §12). */
export const SCHEDULED_LIMIT_PERMISSION = 'export_hub_scheduled_export_limit_'
export const ONE_TIME_LIMIT_PERMISSION = 'export_hub_one_time_export_limit_'

const EMPTY_LIMITS: ExportLimits = {
  scheduledLimit: 0,
  scheduledRemaining: 0,
  oneTimeLimit: 0,
  oneTimeRemaining: 0,
}

/** `Number(value) || 0` made explicit — a permission value is typed `boolean|number|string|null`. */
function permissionCount(value: unknown): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : 0
}

export const useDataExportStore = defineStore('dataExport', () => {
  const jobs = ref<ExportJob[]>([])
  const limits = ref<ExportLimits>({ ...EMPTY_LIMITS })
  const templates = ref<ExportTemplate[]>([])
  const breakdownCombinations = ref<string[][]>([])
  const draft = ref<ExportJobDraft | null>(null)
  const creating = ref(false)

  /**
   * `DataExportTable.vue:280-284`. The two predicates are NOT complements: a one-time job must be
   * `update_frequency === 'once'` AND not explicitly flagged `is_one_time: false`, so a `once` job
   * carrying `is_one_time: false` lands in NEITHER tab. That asymmetry is legacy behaviour and is
   * preserved rather than "fixed" — the flag is the backend's own re-classification signal.
   */
  const oneTimeJobs = computed(() =>
    jobs.value.filter(
      (job) =>
        job.common_fields?.is_one_time !== false && job.common_fields?.update_frequency === 'once',
    ),
  )
  const scheduledJobs = computed(() =>
    jobs.value.filter((job) => job.common_fields?.update_frequency !== 'once'),
  )

  /**
   * Used = entitlement − remaining (`DataExportTable.vue:232-236`). Clamped at zero: the server
   * value is unvalidated, and legacy's bare subtraction rendered `-2/0` whenever `remaining`
   * outran the package limit (standing rule — unclamped server values gain a guard on the port).
   */
  const scheduledUsed = computed(() =>
    Math.max(0, limits.value.scheduledLimit - limits.value.scheduledRemaining),
  )
  const oneTimeUsed = computed(() =>
    Math.max(0, limits.value.oneTimeLimit - limits.value.oneTimeRemaining),
  )

  const canCreateScheduled = computed(() => limits.value.scheduledRemaining > 0)
  const canCreateOneTime = computed(() => limits.value.oneTimeRemaining > 0)

  /** `checkName` (`DataExportCreate.vue:470-472`) — an exact match against the loaded jobs. */
  function isNameTaken(name: string): boolean {
    return jobs.value.some((job) => job.common_fields?.name === name)
  }

  async function loadJobs(): Promise<void> {
    const { accountUserId } = useAccount()
    const { workspaceId } = useWorkspace()
    if (accountUserId.value === undefined || workspaceId.value === undefined) return
    jobs.value = await exportJobService.loadJobs(accountUserId.value, workspaceId.value)
  }

  async function loadLimits(): Promise<void> {
    const { accountId } = useAccount()
    if (accountId.value === undefined) return
    const { permissions } = useRbac()
    const remaining = await exportJobService.loadRemainingCounts(accountId.value)
    limits.value = {
      scheduledLimit: permissionCount(permissions.value[SCHEDULED_LIMIT_PERMISSION]),
      scheduledRemaining: remaining.scheduled,
      oneTimeLimit: permissionCount(permissions.value[ONE_TIME_LIMIT_PERMISSION]),
      oneTimeRemaining: remaining.oneTime,
    }
  }

  /**
   * Templates are platform-scoped, so a failed load must not leave the previous platform's
   * catalog on screen — it is cleared BEFORE the request and the `ApiError` reaches the caller
   * (legacy swallowed it into an empty list, `DataExportCreate.vue:457-461`).
   */
  async function loadTemplates(platforms: readonly string[]): Promise<void> {
    templates.value = []
    breakdownCombinations.value = []
    const catalog = await exportJobService.loadTemplates(platforms)
    templates.value = catalog.templates
    breakdownCombinations.value = catalog.breakdownCombinations
  }

  function startDraft(now?: Date): ExportJobDraft {
    const next = createExportJobDraft(now)
    draft.value = next
    return next
  }

  function updateDraft(patch: Partial<ExportJobDraft>): void {
    if (!draft.value) return
    draft.value = { ...draft.value, ...patch }
  }

  function resetDraft(): void {
    draft.value = null
    creating.value = false
  }

  function setCreating(value: boolean): void {
    creating.value = value
  }

  /** Clears EVERY field. Fires on workspace switch and on logout. */
  function reset(): void {
    jobs.value = []
    limits.value = { ...EMPTY_LIMITS }
    templates.value = []
    breakdownCombinations.value = []
    resetDraft()
  }

  return {
    jobs,
    limits,
    templates,
    breakdownCombinations,
    draft,
    creating,
    oneTimeJobs,
    scheduledJobs,
    scheduledUsed,
    oneTimeUsed,
    canCreateScheduled,
    canCreateOneTime,
    isNameTaken,
    loadJobs,
    loadLimits,
    loadTemplates,
    startDraft,
    updateDraft,
    resetDraft,
    setCreating,
    reset,
  }
})

export type DataExportStore = ReturnType<typeof useDataExportStore>
